// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import XngZone from "@guestsupply/App/Components/Common/XngZone";
import { checkoutPages } from "@guestsupply/XDL/constants";
import { xScreen } from "@guestsupply/XDL/Utilities";
import color from "@guestsupply/XDL/Utilities/color";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";
import styled from "styled-components";

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    linkList1GridItem?: GridItemProps;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    linkList4GridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
    socialLinksGridItem?: GridItemProps;
    siteMapLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;

    // XNG-CHANGES
    containerStyledWrapper?: InjectableCss;
    topSectionStyledWrapper?: InjectableCss;
    topSectionGridContainer?: GridContainerProps;
    socialIconsGridItem?: GridItemProps;
    bottomSectionStyledWrapper?: InjectableCss;
    copyRightGridItem?: InjectableCss;
    termsGridItem?: InjectableCss;
    middleSectionGridItem?: GridItemProps;
    middleSectionGridContainer?: GridContainerProps;
}

export const footerContainerStyles: FooterContainerStyles = {
    logoGridItem: {
        width: 12,
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
            img {
                height: 78px;
            }
        `,
    },
    linkList1GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList2GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList3GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList4GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    subscribeGridItem: {
        width: [12, 12, 8, 4, 4],
    },
    socialLinksGridItem: {
        width: 12,
    },
    siteMapLinksGridItem: {
        width: 12,
    },
    richContentGridItem: {
        width: 12,
    },
    logoWrapper: {
        css: css`
            width: 110px;
        `,
    },
};

export const FooterMiddleSection = styled.div`
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    margin-bottom: 24px;
    margin-top: 24px;
    row-gap: 16px;
`;

export const GeneralLinksWrapper = styled.div`
    &:not(:last-child) {
        flex: 1;
        min-width: 230px;
        padding-right: 22px;
    }
    &:last-child {
        flex: auto;
        ${xScreen("gt", "md")} {
            max-width: 450px;
        }
        .list-wrapper .links-wrapper {
            width: 50%;
            padding-right: 20px;
        }
    }
    ${xScreen("lte", "md")} {
        &&& {
            min-width: calc(100% / 3 - 20px);
            margin-bottom: 16px;
            &:last-child {
                min-width: calc(2 * 100% / 3);
                margin-right: 0;
            }
        }
    }
    ${xScreen("lte", "sm")} {
        &&&& {
            min-width: calc(50% - 12px);
            margin-bottom: 20px;
            width: calc(50% - 12px);
            &:last-child {
                width: 100%;
            }
        }
    }
    .list-wrapper {
        padding: 0;
        .title-wrapper {
            margin-bottom: 15px;
        }
        .title-wrapper .list-title > span {
            color: ${color("common.ui1")};
            font-size: 16px;
            line-height: 19px;
            &:hover {
                color: ${color("common.ui8")};
            }
        }
        .links-wrapper {
            margin-bottom: 12px;
            line-height: 15px;
            padding: 0;
            &:last-child {
                margin-bottom: 0;
            }
            .list-link {
                > span {
                    color: ${color("common.ui4")};
                    font-size: 12px;
                    font-weight: bold;
                    line-height: 15px;
                    text-transform: initial;
                }
                &:hover > span {
                    color: ${color("common.ui8")};
                }
            }
        }
    }
`;

const FooterContainer: FC<OwnProps> = ({ id, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));

    // <XNG-Changes>
    const { isCheckoutPage } = useAppSelector(state => ({
        isCheckoutPage: checkoutPages.includes(getCurrentPage(state).type),
    }));
    // </XNG-Changes>
    const copyrightMessage = () => {
        const message = siteMessage("Custom_Footer_Copyright") as string;
        return message.replace("{0}", String(new Date().getFullYear()));
    };

    return (
        <StyledWrapper {...styles.containerStyledWrapper}>
            {!isCheckoutPage && (
                <>
                    <GridContainer {...styles.topSectionGridContainer}>
                        <GridItem {...styles.subscribeGridItem}>
                            <XngZone hint="Subscribe" contentId={id} zoneName="Subscribe" />
                        </GridItem>
                        <GridItem {...styles.socialIconsGridItem}>
                            <XngZone hint="Social Icons" contentId={id} zoneName="Social Icons" />
                        </GridItem>
                    </GridContainer>
                    <GridContainer {...styles.middleSectionGridContainer}>
                        <GridItem {...styles.middleSectionGridItem}>
                            <FooterMiddleSection>
                                <GeneralLinksWrapper>
                                    <Zone zoneName="LinkList1" contentId={id} />
                                </GeneralLinksWrapper>
                                <GeneralLinksWrapper>
                                    <Zone zoneName="LinkList2" contentId={id} />
                                </GeneralLinksWrapper>
                                <GeneralLinksWrapper>
                                    <Zone zoneName="LinkList3" contentId={id} />
                                </GeneralLinksWrapper>
                                <GeneralLinksWrapper>
                                    <Zone zoneName="LinkList4" contentId={id} />
                                </GeneralLinksWrapper>
                                <GeneralLinksWrapper>
                                    <Zone zoneName="LinkList5" contentId={id} />
                                </GeneralLinksWrapper>
                            </FooterMiddleSection>
                        </GridItem>
                    </GridContainer>
                </>
            )}

            <StyledWrapper {...styles.bottomSectionStyledWrapper}>
                <StyledWrapper {...styles.copyRightGridItem} className="copy-right">
                    <Typography as="p">{copyrightMessage()}</Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.termsGridItem} className="terms">
                    <XngZone hint="Terms" contentId={id} zoneName="Terms" />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
    },
};

export default footerContainer;
